import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase-config';
import { 
  CheckCircleIcon,
  ExclamationIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  SparklesIcon,
  UserIcon,
  PhoneIcon,
  MailIcon,
  CurrencyDollarIcon
} from '@heroicons/react/outline';
import AuthModal from '../components/AuthModal';
import DynamicField from '../components/DynamicField';
import { saveQuoteDraft, getQuoteDraft, clearQuoteDraft } from '../utils/quoteStorage';
import LocationPicker from '../components/LocationPicker';
import { getFormConfig, getFieldConfig } from '../utils/categoryForms';

const CreateRFQ = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedImages, setSelectedImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(0); // 0 = category selection, 1+ = form steps
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [submittedRFQ, setSubmittedRFQ] = useState(null); // Store submitted RFQ data
  
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    quantity: '',
    frequency: '', // one-time or recurring
    urgency: '',
    budget: '',
    timeline: '',
    location: '',
    locationData: null,
    locationType: 'everywhere', // everywhere or specific
    targetRadius: 100,
    fullName: '',
    whatsapp: '',
    email: '',
    allowWhatsapp: true,
    // Category-specific fields
    specifications: '',
    samples: { choice: null, files: [] },
    repairPhotos: { choice: null, files: [] },
    propertyType: '',
    bedrooms: '',
    bathrooms: '',
    furnished: '',
    leaseDuration: '',
    moveInDate: '',
    maxRent: '',
    serviceType: '',
    propertySize: '',
    quality: '',
    workType: '',
    complexity: ''
  });
  
  const { user, createRFQ } = useAuth();
  const navigate = useNavigate();
  
  // Handle navigation state for pre-selected category and initial step
  React.useEffect(() => {
    const location = window.location;
    if (location.state) {
      const { selectedCategory, categoryId, initialStep } = location.state;
      
      if (selectedCategory) {
        setFormData(prev => ({
          ...prev,
          category: selectedCategory
        }));
      }
      
      if (initialStep && typeof initialStep === 'number') {
        setCurrentStep(initialStep);
      } else if (selectedCategory) {
        // If category is selected but no initial step specified, go to step 2
        setCurrentStep(2);
      }
    }
  }, []);

  // Skeleton loading components
  const SkeletonField = ({ className = "" }) => (
    <div className={`animate-pulse ${className}`}>
      <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
      <div className="h-12 bg-gray-200 rounded-xl"></div>
    </div>
  );

  const SkeletonButton = ({ className = "" }) => (
    <div className={`animate-pulse ${className}`}>
      <div className="h-12 bg-gray-200 rounded-xl"></div>
    </div>
  );

  const SkeletonCategory = () => (
    <div className="animate-pulse p-3 sm:p-4 border-2 border-gray-200 rounded-xl">
      <div className="w-8 h-8 bg-gray-200 rounded mb-2"></div>
      <div className="h-4 bg-gray-200 rounded w-20 mb-1"></div>
      <div className="h-3 bg-gray-200 rounded w-32"></div>
    </div>
  );

  // Fetch categories from backend API
  React.useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch('https://us-central1-twerceapp.cloudfunctions.net/getCategories');
        const data = await response.json();
        
        if (data.success && data.categories) {
          // Sort categories to prioritize specific ones
          const sortedCategories = data.categories.sort((a, b) => {
            // Priority categories that should come first
            const priorityCategories = ['Printing & Packaging', 'Housing & Rentals'];
            
            const aIndex = priorityCategories.indexOf(a.name);
            const bIndex = priorityCategories.indexOf(b.name);
            
            // If both are priority categories, maintain their order
            if (aIndex !== -1 && bIndex !== -1) {
              return aIndex - bIndex;
            }
            
            // If only a is priority, a comes first
            if (aIndex !== -1) {
              return -1;
            }
            
            // If only b is priority, b comes first
            if (bIndex !== -1) {
              return 1;
            }
            
            // If neither is priority, maintain alphabetical order
            return a.name.localeCompare(b.name);
          });
          
          setCategories(sortedCategories);
        } else {
          console.error('Failed to fetch categories:', data.error);
          setError('Failed to load categories. Please refresh the page.');
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        setError('Failed to load categories. Please refresh the page.');
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  // Check for saved draft on component mount
  React.useEffect(() => {
    const draft = getQuoteDraft();
    if (draft && draft.action === 'create_rfq' && draft.formData) {
      setFormData(draft.formData);
      if (draft.selectedImages) {
        setSelectedImages(draft.selectedImages);
      }
      // Clear the draft after restoring
      clearQuoteDraft();
    }
  }, []);

  const handleLocationChange = (locationData) => {
    setFormData(prev => ({
      ...prev,
      locationData: locationData,
      location: locationData?.name || ''
    }));
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const uploadImages = async () => {
    if (selectedImages.length === 0) return [];

    setUploadingImages(true);
    const uploadPromises = selectedImages.map(async (imageData) => {
      const fileName = `rfq-images/${user.uid}/${Date.now()}-${imageData.file.name}`;
      const storageRef = ref(storage, fileName);
      
      try {
        const snapshot = await uploadBytes(storageRef, imageData.file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        return downloadURL;
      } catch (error) {
        console.error('Error uploading image:', error);
        throw error;
      }
    });

    try {
      const imageUrls = await Promise.all(uploadPromises);
      setUploadingImages(false);
      return imageUrls;
    } catch (error) {
      setUploadingImages(false);
      throw error;
    }
  };

  const uploadRepairPhotos = async () => {
    if (!formData.repairPhotos?.files || formData.repairPhotos.files.length === 0) return [];

    setUploadingImages(true);
    const uploadPromises = formData.repairPhotos.files.map(async (file) => {
      const fileName = `repair-photos/${user.uid}/${Date.now()}-${file.name}`;
      const storageRef = ref(storage, fileName);
      
      try {
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        return downloadURL;
      } catch (error) {
        console.error('Error uploading repair photo:', error);
        throw error;
      }
    });

    try {
      const photoUrls = await Promise.all(uploadPromises);
      setUploadingImages(false);
      return photoUrls;
    } catch (error) {
      setUploadingImages(false);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields before submission
    const requiredFields = {
      title: 'Title',
      category: 'Category',
      quantity: 'Quantity',
      frequency: 'Frequency',
      urgency: 'Timeline',
      location: 'Location',
      fullName: 'Full Name',
      whatsapp: 'WhatsApp Number'
    };

    const missingFields = [];
    for (const [field, label] of Object.entries(requiredFields)) {
      if (!formData[field] || formData[field].toString().trim() === '') {
        missingFields.push(label);
      }
    }

    if (missingFields.length > 0) {
      setError(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }
    
    // Allow submission without authentication
    // Authentication will be required only for tracking

    setLoading(true);
    setError('');
    setSuccess('');

    console.log('Submitting RFQ with data:', formData);

    try {
      // Upload images first
      const imageUrls = await uploadImages();
      console.log('Uploaded images:', imageUrls);

      // Upload repair photos if any
      const repairPhotoUrls = await uploadRepairPhotos();
      console.log('Uploaded repair photos:', repairPhotoUrls);

      // Prepare RFQ data in the format expected by AuthContext
      const rfqData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        quantity: formData.quantity,
        frequency: formData.frequency,
        urgency: formData.urgency,
        budget: formData.budget,
        location: formData.location,
        locationData: formData.locationData,
        fullName: formData.fullName,
        whatsapp: formData.whatsapp,
        email: formData.email,
        allowWhatsapp: formData.allowWhatsapp,
        media: imageUrls, // Add uploaded image URLs
        targetRadius: formData.targetRadius,
        // Category-specific fields
        specifications: formData.specifications,
        samples: formData.samples?.files || [],
        repairPhotos: repairPhotoUrls,
        propertyType: formData.propertyType,
        bedrooms: formData.bedrooms,
        bathrooms: formData.bathrooms,
        furnished: formData.furnished,
        leaseDuration: formData.leaseDuration,
        moveInDate: formData.moveInDate,
        maxRent: formData.maxRent,
        serviceType: formData.serviceType,
        propertySize: formData.propertySize,
        quality: formData.quality,
        workType: formData.workType,
        complexity: formData.complexity
      };

      console.log('Calling createRFQ with data:', rfqData);

      // Use the createRFQ function from AuthContext
      const result = await createRFQ(rfqData);
      
      console.log('createRFQ result:', result);
    
      if (result.success) {
        // Store the submitted RFQ data for tracking
        setSubmittedRFQ({
          rfqId: result.rfqId,
          title: formData.title,
          category: formData.category,
          timestamp: new Date().toISOString()
        });
        
        setSuccess('Your request has been posted successfully!');
        // Don't redirect - show the success options instead
      } else {
        setError(`Failed to post your need: ${result.error}`);
      }
    } catch (err) {
      setError('Failed to post your need. Please try again.');
      console.error('Error creating RFQ:', err);
    }
    
    setLoading(false);
  };

  const nextStep = () => {
    // Step 0: Category selection - no validation needed, just move to next step
    if (currentStep === 0) {
      if (!formData.category) {
        setError('Please select a category to continue');
        return;
      }
      setError('');
      setCurrentStep(1);
      return;
    }

    const formConfig = getFormConfig(formData.category);
    const currentStepConfig = formConfig.steps.find(step => step.id === currentStep);
    
    if (!currentStepConfig) {
      setError('Step configuration not found');
      return;
    }

    // Validate required fields for current step
    const requiredFields = currentStepConfig.fields.filter(fieldName => {
      // Skip validation for certain fields
      if (fieldName === 'preview') return false;
      if (fieldName === 'locationData' || fieldName === 'targetRadius') return false;
      if (fieldName === 'samples') return false; // Samples are always optional
      if (fieldName === 'repairPhotos') return false; // Repair photos are always optional
      
      const fieldConfig = getFieldConfig(formData.category, fieldName);
      return fieldConfig?.required !== false;
    });

    const missingFields = [];
    for (const fieldName of requiredFields) {
      const value = formData[fieldName];
      if (!value || value.toString().trim() === '') {
        const fieldConfig = getFieldConfig(formData.category, fieldName);
        missingFields.push(fieldConfig?.label || fieldName);
      }
    }

    if (missingFields.length > 0) {
      setError(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    // Clear any previous errors
    setError('');
    
    const maxSteps = formConfig.steps.length;
    if (currentStep < maxSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else if (currentStep === 1) {
      setCurrentStep(0); // Go back to category selection
    }
  };

  const handleAuthSuccess = () => {
    // After successful authentication, submit the form
    handleSubmit(new Event('submit'));
  };

  // Success screen component
  const renderSuccessScreen = () => {
    return (
      <div className="w-full text-center">
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
            <span className="text-4xl">✅</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Request Posted Successfully!</h2>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Vendors will start sending you offers shortly. Keep an eye out — most respond within 24 hours.
          </p>
          
          {/* RFQ Details Card */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm mb-8 max-w-md mx-auto">
            <h3 className="font-semibold text-gray-900 mb-3">Request Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Request ID:</span>
                <span className="font-medium text-gray-900">{submittedRFQ?.rfqId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Category:</span>
                <span className="font-medium text-gray-900">{submittedRFQ?.category}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Posted:</span>
                <span className="font-medium text-gray-900">
                  {submittedRFQ?.timestamp ? new Date(submittedRFQ.timestamp).toLocaleDateString() : 'Just now'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-4 max-w-md mx-auto">
          <button
            onClick={() => setShowAuthModal(true)}
            className="w-full flex items-center justify-center px-6 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            <span className="mr-2">📊</span>
            Track My Request
          </button>
          
          <button
            onClick={() => {
              setFormData({
                title: '',
                category: '',
                description: '',
                quantity: '',
                frequency: '',
                urgency: '',
                budget: '',
                timeline: '',
                location: '',
                locationData: null,
                locationType: 'everywhere',
                targetRadius: 100,
                fullName: '',
                whatsapp: '',
                email: '',
                allowWhatsapp: true,
                specifications: '',
                samples: { choice: null, files: [] },
                repairPhotos: { choice: null, files: [] },
                propertyType: '',
                bedrooms: '',
                bathrooms: '',
                furnished: '',
                leaseDuration: '',
                moveInDate: '',
                maxRent: '',
                serviceType: '',
                propertySize: '',
                quality: '',
                workType: '',
                complexity: ''
              });
              setCurrentStep(0);
              setSubmittedRFQ(null);
              setSuccess('');
              setError('');
            }}
            className="w-full flex items-center justify-center px-6 py-4 border-2 border-gray-300 text-gray-700 rounded-xl hover:border-gray-400 hover:bg-gray-50 transition-all duration-200 font-medium"
          >
            <span className="mr-2">📝</span>
            Post Another Request
          </button>
          
          <button
            onClick={() => navigate('/')}
            className="w-full flex items-center justify-center px-6 py-4 border-2 border-gray-300 text-gray-700 rounded-xl hover:border-gray-400 hover:bg-gray-50 transition-all duration-200 font-medium"
          >
            <span className="mr-2">🏠</span>
            Go to Homepage
          </button>
        </div>
      </div>
    );
  };

  // Dynamic step rendering based on category
  const renderStep = () => {
    // Show success screen if RFQ was submitted
    if (submittedRFQ) {
      return renderSuccessScreen();
    }
    
    // Step 0: Category selection
    if (currentStep === 0) {
      return (
        <div className="w-full">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">What do you need?</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">Tell us what you need — we'll alert the right vendors to respond instantly.</p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {loadingCategories ? (
              // Skeleton loading for categories
              Array.from({ length: 6 }).map((_, index) => (
                <SkeletonCategory key={index} />
              ))
            ) : (
              categories.map((category, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setFormData(prev => ({ 
                      ...prev, 
                      category: category.name,
                      // Set default values for category-specific fields
                      samples: category.name === 'Printing & Packaging' ? { choice: 'no', files: [] } : prev.samples,
                      repairPhotos: category.name === 'Trade & Repairs' ? { choice: null, files: [] } : prev.repairPhotos
                    }));
                    setCurrentStep(1); // Move to first form step
                  }}
                  className={`p-3 sm:p-4 border-2 rounded-xl transition-all text-left touch-manipulation hover:border-orange-300 hover:bg-orange-50 ${
                    formData.category === category.name
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="text-xl sm:text-2xl mb-2">{category.icon || "📦"}</div>
                  <div className="text-sm font-medium mb-1">{category.name}</div>
                  <div className="text-xs text-gray-600 leading-tight">{category.description || category.name}</div>
                </button>
              ))
            )}
          </div>
        </div>
      );
    }

    // Regular form steps (1+)
    const formConfig = getFormConfig(formData.category);
    const currentStepConfig = formConfig.steps.find(step => step.id === currentStep);
    
    if (!currentStepConfig) {
      return <div>Step not found</div>;
    }

    const renderFields = () => {
      return currentStepConfig.fields.map(fieldName => {
        // Category selection is now handled in step 0, so skip it here
        if (fieldName === 'category') {
          return null;
        }

        // Special handling for title/description
        if (fieldName === 'title') {
          const getPlaceholder = (category) => {
            switch (category) {
              case 'Printing & Packaging':
                return "e.g., I need 500 branded paper bags for my restaurant, A4 size, 300gsm paper, full color printing with my logo, matte finish, needed within 1 week...";
              case 'Housing & Rentals':
                return "e.g., I'm looking for a 2-bedroom apartment in Lekki Phase 1, preferably furnished, with parking space, security, and close to shopping centers. Budget ₦800k-1.2M monthly...";
              case 'Cleaning Services':
                return "e.g., I need deep cleaning services for my 3-bedroom apartment in Victoria Island, including kitchen, bathrooms, and living areas. Moving out cleaning required...";
              case 'Food Supply':
                return "e.g., I need 50kg of premium rice, 20kg of beans, and 10kg of garri for my restaurant. Looking for bulk suppliers who can deliver weekly...";
              case 'Trade & Repairs':
                return "e.g., I need an electrician to fix the wiring in my office building, 5 rooms need new outlets and lighting fixtures installed. Urgent repair needed...";
              default:
                return "Tell us exactly what you need. Be specific about quantity, quality, timeline, and any special requirements...";
            }
          };

          return (
            <div key={fieldName} className="mb-6 sm:mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">Describe your requirement *</label>
              <textarea
                placeholder={getPlaceholder(formData.category)}
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                rows={4}
                className="w-full px-3 sm:px-4 py-3 sm:py-4 text-base sm:text-lg border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors resize-none"
              />
            </div>
          );
        }

        // Special handling for location
        if (fieldName === 'location' || fieldName === 'locationData' || fieldName === 'targetRadius') {
          return (
            <div key="location" className="mb-6 sm:mb-8">
              <div className="space-y-3 sm:space-y-4">
                {/* Everywhere Option */}
                <button
                  onClick={() => {
                    setFormData(prev => ({ 
                      ...prev, 
                      location: 'Everywhere',
                      locationData: null,
                      targetRadius: null,
                      locationType: 'everywhere'
                    }));
                  }}
                  className={`w-full flex items-center p-3 sm:p-4 border-2 rounded-xl transition-all text-left touch-manipulation ${
                    formData.locationType === 'everywhere' || (!formData.locationData && !formData.location)
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                  }`}
                >
                  <span className="text-xl sm:text-2xl mr-3">🌍</span>
                  <div>
                    <span className="font-medium text-sm sm:text-base">Everywhere</span>
                    <div className="text-xs sm:text-sm text-gray-600">Get quotes from vendors nationwide</div>
                  </div>
                </button>

                {/* Specific Location Option */}
                <button
                  onClick={() => {
                    setFormData(prev => ({ 
                      ...prev, 
                      locationType: 'specific',
                      location: '',
                      locationData: null
                    }));
                  }}
                  className={`w-full flex items-center p-3 sm:p-4 border-2 rounded-xl transition-all text-left touch-manipulation ${
                    formData.locationType === 'specific'
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                  }`}
                >
                  <span className="text-xl sm:text-2xl mr-3">📍</span>
                  <div>
                    <span className="font-medium text-sm sm:text-base">Specific Area</span>
                    <div className="text-xs sm:text-sm text-gray-600">Target vendors in a specific location</div>
                  </div>
                </button>
              </div>

              {/* Show LocationPicker only if specific location is selected */}
              {formData.locationType === 'specific' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-3">Target Area:</label>
                  <LocationPicker
                    onLocationSelect={handleLocationChange}
                    selectedLocation={formData.locationData}
                    showRadius={true}
                    radius={formData.targetRadius || 100}
                    onRadiusChange={(radius) => setFormData(prev => ({ ...prev, targetRadius: radius }))}
                    placeholder="Search for your target area..."
                    className="w-full"
                    radiusLabel="Target Radius"
                  />
                </div>
              )}
            </div>
          );
        }

        // Special handling for budget
        if (fieldName === 'budget') {
          const fieldConfig = { type: 'budget', label: 'Budget' };
          return (
            <DynamicField
              key={fieldName}
              fieldName={fieldName}
              fieldConfig={fieldConfig}
              value={formData[fieldName]}
              onChange={handleFieldChange}
            />
          );
        }

        // Special handling for urgency
        if (fieldName === 'urgency') {
          const fieldConfig = { type: 'urgency', label: 'Timeline' };
          return (
            <DynamicField
              key={fieldName}
              fieldName={fieldName}
              fieldConfig={fieldConfig}
              value={formData[fieldName]}
              onChange={handleFieldChange}
            />
          );
        }

        // Special handling for contact information
        if (fieldName === 'fullName' || fieldName === 'whatsapp' || fieldName === 'email' || fieldName === 'allowWhatsapp') {
          if (fieldName === 'fullName') {
            return (
              <div key={fieldName} className="mb-6 sm:mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  placeholder="Enter your full name"
                  value={formData.fullName}
                  onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                  className="w-full px-3 sm:px-4 py-3 sm:py-4 text-base sm:text-lg border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                />
              </div>
            );
          }
          if (fieldName === 'whatsapp') {
            return (
              <div key={fieldName} className="mb-6 sm:mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp Number *</label>
                <input
                  type="tel"
                  placeholder="e.g., +234 801 234 5678"
                  value={formData.whatsapp}
                  onChange={(e) => setFormData(prev => ({ ...prev, whatsapp: e.target.value }))}
                  className="w-full px-3 sm:px-4 py-3 sm:py-4 text-base sm:text-lg border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                />
              </div>
            );
          }
          if (fieldName === 'email') {
            return (
              <div key={fieldName} className="mb-6 sm:mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address (Optional)</label>
                <input
                  type="email"
                  placeholder="your.email@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 sm:px-4 py-3 sm:py-4 text-base sm:text-lg border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                />
              </div>
            );
          }
          if (fieldName === 'allowWhatsapp') {
            return (
              <div key={fieldName} className="mb-6 sm:mb-8">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="allowWhatsapp"
                    checked={formData.allowWhatsapp}
                    onChange={(e) => setFormData(prev => ({ ...prev, allowWhatsapp: e.target.checked }))}
                    className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <label htmlFor="allowWhatsapp" className="ml-2 text-gray-700 text-sm sm:text-base">
                    Allow vendors to contact me via WhatsApp
                  </label>
                </div>
              </div>
            );
          }
        }

        // Special handling for preview
        if (fieldName === 'preview') {
          const formatDate = (dateString) => {
            if (!dateString || dateString === 'urgent' || dateString === 'this_week') return dateString;
            try {
              return new Date(dateString).toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              });
            } catch {
              return dateString;
            }
          };

          return (
            <div key={fieldName} className="w-full">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
                  <span className="text-2xl">📋</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Review Your Request</h3>
                <p className="text-gray-600">Please review all details before posting your request</p>
              </div>
              
              <div className="space-y-6">
                {/* Category & Description */}
                <div className="bg-gradient-to-r from-orange-50 to-red-50 p-6 rounded-xl border border-orange-200">
                  <div className="flex items-center mb-4">
                    <span className="text-2xl mr-3">📦</span>
                    <h4 className="text-lg font-semibold text-gray-900">Request Details</h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <span className="text-orange-600 font-medium min-w-[100px]">Category:</span>
                      <span className="text-gray-700">{formData.category}</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-orange-600 font-medium min-w-[100px]">Description:</span>
                      <span className="text-gray-700">{formData.title}</span>
                    </div>
                  </div>
                </div>

                {/* Specifications */}
                {(formData.quantity || formData.frequency || formData.specifications) && (
                  <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                    <div className="flex items-center mb-4">
                      <span className="text-2xl mr-3">⚙️</span>
                      <h4 className="text-lg font-semibold text-gray-900">Specifications</h4>
                    </div>
                    <div className="space-y-3">
                      {formData.quantity && (
                        <div className="flex items-center">
                          <span className="text-gray-600 font-medium min-w-[100px]">Quantity:</span>
                          <span className="text-gray-700">{formData.quantity}</span>
                        </div>
                      )}
                      {formData.frequency && (
                        <div className="flex items-center">
                          <span className="text-gray-600 font-medium min-w-[100px]">Frequency:</span>
                          <span className="text-gray-700 capitalize">{formData.frequency.replace('-', ' ')}</span>
                        </div>
                      )}
                      {formData.specifications && (
                        <div className="flex items-start">
                          <span className="text-gray-600 font-medium min-w-[100px]">Specifications:</span>
                          <span className="text-gray-700">{formData.specifications}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Timeline & Location */}
                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <span className="text-2xl mr-3">📅</span>
                    <h4 className="text-lg font-semibold text-gray-900">Timeline & Location</h4>
                  </div>
                  <div className="space-y-3">
                    {formData.urgency && (
                      <div className="flex items-center">
                        <span className="text-gray-600 font-medium min-w-[100px]">Timeline:</span>
                        <span className="text-gray-700">{formatDate(formData.urgency)}</span>
                      </div>
                    )}
                    {formData.location && (
                      <div className="flex items-center">
                        <span className="text-gray-600 font-medium min-w-[100px]">Location:</span>
                        <span className="text-gray-700">{formData.location}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Budget */}
                {formData.budget && (
                  <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                    <div className="flex items-center mb-4">
                      <span className="text-2xl mr-3">💰</span>
                      <h4 className="text-lg font-semibold text-gray-900">Budget</h4>
                    </div>
                    <div className="text-lg font-medium text-gray-700">{formData.budget}</div>
                  </div>
                )}

                {/* Contact Information */}
                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex items-center mb-4">
                    <span className="text-2xl mr-3">👤</span>
                    <h4 className="text-lg font-semibold text-gray-900">Contact Information</h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <span className="text-gray-600 font-medium min-w-[100px]">Name:</span>
                      <span className="text-gray-700">{formData.fullName}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-gray-600 font-medium min-w-[100px]">WhatsApp:</span>
                      <span className="text-gray-700">{formData.whatsapp}</span>
                    </div>
                    {formData.email && (
                      <div className="flex items-center">
                        <span className="text-gray-600 font-medium min-w-[100px]">Email:</span>
                        <span className="text-gray-700">{formData.email}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Uploaded Files */}
                {(formData.repairPhotos?.files?.length > 0 || formData.samples?.files?.length > 0) && (
                  <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                    <div className="flex items-center mb-4">
                      <span className="text-2xl mr-3">📎</span>
                      <h4 className="text-lg font-semibold text-gray-900">Uploaded Files</h4>
                    </div>
                    <div className="space-y-3">
                      {formData.repairPhotos?.files?.length > 0 && (
                        <div className="flex items-center">
                          <span className="text-gray-600 font-medium min-w-[100px]">Repair Photos:</span>
                          <span className="text-gray-700">{formData.repairPhotos.files.length} photo(s)</span>
                        </div>
                      )}
                      {formData.samples?.files?.length > 0 && (
                        <div className="flex items-center">
                          <span className="text-gray-600 font-medium min-w-[100px]">Samples:</span>
                          <span className="text-gray-700">{formData.samples.files.length} file(s)</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Success Message */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200 text-center">
                  <div className="text-3xl mb-3">✅</div>
                  <h4 className="text-lg font-semibold text-green-800 mb-2">Ready to Post!</h4>
                  <p className="text-green-700">Your request looks great. Click "Post Request" to submit and get quotes from vendors.</p>
                </div>
              </div>
            </div>
          );
        }

        // Handle category-specific fields
        const fieldConfig = getFieldConfig(formData.category, fieldName);
        if (fieldConfig) {
          return (
            <DynamicField
              key={fieldName}
              fieldName={fieldName}
              fieldConfig={fieldConfig}
              value={formData[fieldName]}
              onChange={handleFieldChange}
            />
          );
        }

        // Default field rendering
        return (
          <div key={fieldName} className="mb-6 sm:mb-8">
            <label className="block text-sm font-medium text-gray-700 mb-2">{fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}</label>
            <input
              type="text"
              placeholder={`Enter ${fieldName}`}
              value={formData[fieldName] || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, [fieldName]: e.target.value }))}
              className="w-full px-3 sm:px-4 py-3 sm:py-4 text-base sm:text-lg border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
            />
          </div>
        );
      });
    };

    return (
      <div className="w-full">
        <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">{currentStepConfig.title}</h2>
        <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">{currentStepConfig.description}</p>
        {renderFields()}
      </div>
    );

  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50">
      {/* Mobile-optimized container with better padding */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-8">
        {/* Mobile-optimized Header - Minimal on mobile, full on desktop */}
        <div className="text-center mb-4 sm:mb-8">
          {/* Mobile: Compact header */}
          <div className="block sm:hidden">
            <div className="inline-flex items-center px-2 py-1 bg-gradient-to-r from-orange-100 to-red-100 text-orange-700 rounded-full text-xs font-medium mb-2 border border-orange-200">
              <SparklesIcon className="w-3 h-3 mr-1" />
              <span className="w-1 h-1 bg-orange-500 rounded-full mr-1 animate-pulse"></span>
              Post Request
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              What do you need?
            </h1>
            <p className="text-sm text-gray-600 mb-4">
              Get competitive quotes from verified vendors
            </p>
            
            {/* Mobile: Collapsible benefits info */}
            <details className="mb-4">
              <summary className="text-xs text-orange-600 font-medium cursor-pointer hover:text-orange-700">
                Why use Twerce? ▼
              </summary>
              <div className="mt-2 p-3 bg-orange-50 rounded-lg">
                <div className="text-xs space-y-1">
                  <div className="flex items-center">
                    <span className="mr-2">🚀</span>
                    <span>Get 5+ quotes within 24 hours</span>
                  </div>
                  <div className="flex items-center">
                    <span className="mr-2">💰</span>
                    <span>Save up to 40% on procurement</span>
                  </div>
                  <div className="flex items-center">
                    <span className="mr-2">✅</span>
                    <span>All vendors are pre-verified</span>
                  </div>
                </div>
              </div>
            </details>
          </div>

          {/* Desktop: Full header */}
          <div className="hidden sm:block">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-orange-100 to-red-100 text-orange-700 rounded-full text-sm font-medium mb-4 border border-orange-200">
              <SparklesIcon className="w-4 h-4 mr-2" />
              <span className="w-2 h-2 bg-orange-500 rounded-full mr-2 animate-pulse"></span>
              Post Your Request
            </div>
            <h1 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-4 bg-gradient-to-r from-gray-900 via-orange-900 to-gray-900 bg-clip-text text-transparent leading-tight">
              Post Your Business Need
            </h1>
            <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-6">
              Describe what you need and get offers from verified vendors in your area. Save up to 40% on your procurement.
            </p>
            
            {/* Desktop benefits section */}
            <div className="grid grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
              <div className="text-center p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="text-3xl mb-2">🚀</div>
                <h3 className="font-semibold text-gray-900 mb-2 text-base">Fast Response</h3>
                <p className="text-sm text-gray-600">Get 5+ quotes within 24 hours</p>
              </div>
              <div className="text-center p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="text-3xl mb-2">💰</div>
                <h3 className="font-semibold text-gray-900 mb-2 text-base">Save Money</h3>
                <p className="text-sm text-gray-600">Average 40% savings</p>
              </div>
              <div className="text-center p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="text-3xl mb-2">✅</div>
                <h3 className="font-semibold text-gray-900 mb-2 text-base">Verified Vendors</h3>
                <p className="text-sm text-gray-600">Pre-screened suppliers</p>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile-optimized Form Container */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-gray-100 overflow-hidden">
          {/* Error/Success Messages - Mobile optimized */}
          {error && (
            <div className="m-3 sm:m-6 p-3 sm:p-4 bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-xl shadow-sm">
              <div className="flex items-center">
                <ExclamationIcon className="h-4 w-4 sm:h-5 sm:w-5 text-red-500 mr-2 sm:mr-3 flex-shrink-0" />
                <p className="text-red-700 text-sm font-medium">{error}</p>
              </div>
            </div>
          )}
          
          {success && (
            <div className="m-3 sm:m-6 p-3 sm:p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl shadow-sm">
              <div className="flex items-center">
                <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                <p className="text-green-700 text-sm font-medium">{success}</p>
              </div>
            </div>
          )}

          <div className="p-3 sm:p-6 lg:p-8">
            {/* Mobile-optimized Progress Indicator */}
            <div className="mb-4 sm:mb-8">
              {(() => {
                // Step 0: Category selection (no progress bar needed)
                if (currentStep === 0) {
                  return (
                    <div className="text-center mb-4">
                      <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-orange-100 to-red-100 text-orange-700 rounded-full text-sm font-medium border border-orange-200">
                        <span className="w-2 h-2 bg-orange-500 rounded-full mr-2 animate-pulse"></span>
                        Step 1: Choose Category
                      </div>
                    </div>
                  );
                }

                const formConfig = getFormConfig(formData.category);
                const totalSteps = formConfig.steps.length;
                const currentStepConfig = formConfig.steps.find(step => step.id === currentStep);
                
                return (
                  <>
                    {/* Mobile: Simplified progress bar */}
                    <div className="block sm:hidden mb-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Step {currentStep} of {totalSteps}</span>
                        <span className="text-xs text-gray-500">{Math.round((currentStep / totalSteps) * 100)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-orange-500 to-red-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-600 mt-2 text-center">
                        {currentStepConfig?.title || `Step ${currentStep}`}
                      </p>
                    </div>

                    {/* Desktop: Full progress indicator */}
                    <div className="hidden sm:block">
                      <div className="flex items-center justify-center space-x-2">
                        {formConfig.steps.map((step, index) => (
                          <div key={step.id} className="flex items-center">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-300 ${
                              currentStep >= step.id 
                                ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg' 
                                : 'bg-gray-200 text-gray-500'
                            }`}>
                              {currentStep > step.id ? (
                                <CheckCircleIcon className="w-4 h-4" />
                              ) : (
                                step.id
                              )}
                            </div>
                            {index < formConfig.steps.length - 1 && (
                              <div className={`w-8 h-1 mx-1 transition-all duration-300 ${
                                currentStep > step.id ? 'bg-gradient-to-r from-orange-500 to-red-500' : 'bg-gray-200'
                              }`}></div>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="text-center mt-4">
                        <p className="text-sm text-gray-600">
                          Step {currentStep} of {totalSteps}: {currentStepConfig?.title || `Step ${currentStep}`}
                        </p>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Step Content - Mobile optimized */}
            <div className="min-h-[300px] sm:min-h-[400px]">
              {loading ? (
                // Skeleton loading for step content
                <div className="space-y-6">
                  <div className="animate-pulse">
                    <div className="h-8 bg-gray-200 rounded w-48 mb-3"></div>
                    <div className="h-4 bg-gray-200 rounded w-64 mb-6"></div>
                  </div>
                  <SkeletonField />
                  <SkeletonField />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <SkeletonButton />
                    <SkeletonButton />
                  </div>
                </div>
              ) : (
                renderStep()
              )}
            </div>

            {/* Mobile-optimized Navigation Buttons */}
            {!submittedRFQ && (
              <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0 mt-6 sm:mt-10">
              {loading ? (
                // Skeleton loading for navigation buttons
                <>
                  <SkeletonButton className="order-2 sm:order-1" />
                  <SkeletonButton className="order-1 sm:order-2" />
                </>
              ) : (
                <>
                  {currentStep > 0 && (
                    <button
                      type="button"
                      onClick={prevStep}
                      className="order-2 sm:order-1 flex items-center justify-center px-4 sm:px-6 py-3 sm:py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium shadow-sm touch-manipulation"
                    >
                      <ArrowLeftIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                      Back
                    </button>
                  )}
                  
                  {(() => {
                    // Step 0: Show "Continue" button
                    if (currentStep === 0) {
                      return (
                        <button
                          type="button"
                          onClick={nextStep}
                          disabled={!formData.category}
                          className="order-1 sm:order-2 flex items-center justify-center px-6 sm:px-8 py-3 sm:py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none touch-manipulation"
                        >
                          Continue
                          <ArrowRightIcon className="h-4 w-4 sm:h-5 sm:w-5 ml-2" />
                        </button>
                      );
                    }

                    const formConfig = getFormConfig(formData.category);
                    const totalSteps = formConfig.steps.length;
                    
                    return currentStep < totalSteps ? (
                      <button
                        type="button"
                        onClick={nextStep}
                        className="order-1 sm:order-2 flex items-center justify-center px-6 sm:px-8 py-3 sm:py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:scale-105 touch-manipulation"
                      >
                        Next
                        <ArrowRightIcon className="h-4 w-4 sm:h-5 sm:w-5 ml-2" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          console.log('Submit button clicked');
                          console.log('Current form data:', formData);
                          console.log('User:', user);
                          handleSubmit(e);
                        }}
                        disabled={loading}
                        className="order-1 sm:order-2 flex items-center justify-center px-6 sm:px-8 py-3 sm:py-3 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-xl hover:from-green-600 hover:to-teal-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none touch-manipulation"
                      >
                        {loading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white mr-2"></div>
                            <span className="text-sm sm:text-base">Posting RFQ...</span>
                          </>
                        ) : (
                          <>
                            <span className="text-sm sm:text-base">Post RFQ</span>
                            <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 ml-2" />
                          </>
                        )}
                      </button>
                    );
                  })()}
                </>
              )}
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      {showAuthModal && (
        <>
          {console.log('Rendering AuthModal, showAuthModal:', showAuthModal)}
          <AuthModal
            onClose={() => setShowAuthModal(false)}
            onSuccess={handleAuthSuccess}
          />
        </>
      )}
    </div>
  );
};

export default CreateRFQ; 